import { type FC, useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router";

import { ContentTypesEnum, RouteNames } from "../../../../enums";

import Videos from "./Videos";
import EmbeddedVideosOverview from "../../EmbeddedVideos/Overview/EmbeddedVideosOverview";
import VideosSegmentationDropdown from "components/dropdowns/videosSegmentationDropdown/VideosSegmentationDropdown";

export const VideosOverview: FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [videoContext, setVideoContext] = useState<ContentTypesEnum>(
    location.pathname.endsWith("embedded") ? ContentTypesEnum.Embedded : ContentTypesEnum.Uploaded,
  );

  useEffect(() => {
    const newContext = location.pathname.endsWith("embedded") ? ContentTypesEnum.Embedded : ContentTypesEnum.Uploaded;

    setVideoContext(newContext);
  }, [location.pathname]);

  const onContentSegmentationChange = useCallback(
    (context: ContentTypesEnum) => {
      setVideoContext(context);

      navigate(
        context == ContentTypesEnum.Embedded ? `/${RouteNames.contentVideos}/embedded` : `/${RouteNames.contentVideos}`,
        { replace: true },
      );
    },
    [navigate],
  );

  const renderContextSwitcher = useCallback(
    (): React.ReactElement => (
      <VideosSegmentationDropdown onContextChange={onContentSegmentationChange} defaultValue={videoContext} />
    ),
    [onContentSegmentationChange, videoContext],
  );

  const components = useMemo<Partial<Record<ContentTypesEnum, React.ReactElement>>>(
    () => ({
      [ContentTypesEnum.Uploaded]: <Videos renderContentSwitcher={renderContextSwitcher} />,
      [ContentTypesEnum.Embedded]: <EmbeddedVideosOverview renderContentSwitcher={renderContextSwitcher} />,
    }),
    [renderContextSwitcher],
  );

  return components[videoContext];
};

export default VideosOverview;
